import { getLocationPartId } from './locationPartMapping';

function indexLocations(sceneLocations) {
  const byId = {};
  (sceneLocations || []).forEach((loc) => {
    if (loc && loc.id != null) byId[String(loc.id)] = loc;
  });
  return byId;
}

/**
 * Map raw activity API items into the normalized liveEvents shape used by the
 * 3D heatmap, enriching component_id and part_id from scene locations when the
 * API only provides location_id.
 */
export function enrichActivityEvents(items, sceneLocations, modelKeyByComponentId) {
  const locById = indexLocations(sceneLocations);
  return (items || []).map((item) => {
    const locationId = item.location_id != null ? item.location_id : null;
    const loc = locationId != null ? locById[String(locationId)] : null;
    let componentId = item.component_id;
    if (componentId == null) componentId = loc && loc.component_id != null ? loc.component_id : null;
    let partId = item.part_id;
    // part_id is baked in HERE, once, and every later reader prefers it over recomputing
    // (eventHighlightEntries.resolvePartId, eventFilters.resolveEventPartId). So the owning
    // body's model key has to reach this call or the per-shaft bodies resolve the wrong half
    // everywhere downstream.
    if (partId == null) {
      const ownerKey = modelKeyByComponentId && componentId != null
        ? modelKeyByComponentId[String(componentId)]
        : null;
      partId = loc ? getLocationPartId(loc, ownerKey) : null;
    }

    return {
      activity_id: item.activity_id != null ? item.activity_id : null,
      event_type: item.event_type || item.type || 'task',
      title: item.name || item.title || '',
      message: item.description || item.title || item.message || '',
      // Severity is left null when the API omits it rather than defaulted to
      // 'Info'. Only events carry a severity; tasks and repairs carry a
      // `condition` instead, and a fabricated 'Info' classified as a genuine
      // low severity — which made resolveHighlightLevel's condition fallback
      // unreachable and quietly demoted every critical task to level 1.
      severity: item.severity || null,
      // Condition (good/slight/moderate/warning/critical) is the task/repair
      // equivalent of severity; see Task.condition in the activities API.
      condition: item.condition || null,
      timestamp: item.timestamp || null,
      component_id: componentId,
      location_id: locationId,
      part_id: partId,
      component_type: item.component_type || null,
      type: item.type || null,
      tag_id: item.tag_id != null ? item.tag_id : null,
      state: item.state || (item.base_metadata && item.base_metadata.state) || null,
    };
  });
}

/**
 * Identity + mutable-state key used by the reducer to decide whether an
 * incoming poll actually changed anything.
 *
 * severity/condition/state MUST be part of the key. They are the fields
 * resolveHighlightLevel and the closed-activity filter read, and they change
 * on the same activity_id as an alarm escalates (warning -> critical) or is
 * resolved. Keying on activity_id alone made the reducer short-circuit those
 * updates, so an escalating component never turned red and a resolved one
 * never cleared.
 */
export function liveEventDedupKey(event) {
  if (!event) return '';
  const identity = event.activity_id != null
    ? `id:${event.activity_id}`
    : [
      event.type,
      event.event_type,
      event.component_id,
      event.location_id,
      event.timestamp,
      event.message,
    ].join(':');
  return `${identity}|${event.severity || ''}|${event.condition || ''}|${event.state || ''}`;
}
